/**
 * Invoice refunds, credits, adjustments and voids (owner's spec, October 2026): flexible, at the
 * firm's discretion. No refund policy is built in; the code only keeps the totals tied:
 *
 *   invoice total − adjustments − payments + refunds = balance due
 *
 * where "payments" includes credits applied from the client's account, and "refunds" includes
 * paid money moved off the invoice to the client's credit. This is the ONE place the balance is
 * worked out in TypeScript; the database computes the same formula in `invoices.balance_cents`
 * (migration 0059), and a test checks the two agree.
 */
import { z } from "zod";
import { formatCents } from "./money";
import { MAX_INVOICE_CENTS } from "./schemas";

export type InvoiceAmounts = {
  totalCents: number;
  adjustedCents: number;
  /** Every payment received, credits applied included. */
  amountPaidCents: number;
  refundedCents: number;
  creditedCents: number;
};

export type InvoiceBalance = {
  /** What the client still owes (never negative). */
  balanceDueCents: number;
  /** Money on the invoice beyond what's owed: to refund, hold as credit or move to another invoice. */
  overpaidCents: number;
  /** Paid and still on the invoice: the most that can be refunded or moved to credit. */
  netPaidCents: number;
  /** total − adjusted − paid + refunded + credited; negative when overpaid. */
  rawCents: number;
};

export function invoiceBalance(a: InvoiceAmounts): InvoiceBalance {
  const rawCents = a.totalCents - a.adjustedCents - a.amountPaidCents + a.refundedCents + a.creditedCents;
  return {
    rawCents,
    balanceDueCents: Math.max(0, rawCents),
    overpaidCents: Math.max(0, -rawCents),
    netPaidCents: a.amountPaidCents - a.refundedCents - a.creditedCents,
  };
}

/** An issued invoice's status from its amounts: owed → open, nothing owed → paid. */
export function settledStatus(a: InvoiceAmounts): "open" | "paid" {
  return invoiceBalance(a).rawCents > 0 ? "open" : "paid";
}

export const REFUND_METHODS = ["stripe", "check", "transfer", "cash", "other"] as const;
export type RefundMethod = (typeof REFUND_METHODS)[number];
export const REFUND_METHOD_LABELS: Record<RefundMethod, string> = {
  stripe: "Card refund through Stripe",
  check: "Check",
  transfer: "Bank transfer",
  cash: "Cash",
  other: "Other",
};

export const ADJUSTMENT_TYPES = ["courtesy", "fee_waived", "settlement", "write_off", "other"] as const;
export type AdjustmentType = (typeof ADJUSTMENT_TYPES)[number];
export const ADJUSTMENT_LABELS: Record<AdjustmentType, string> = {
  courtesy: "Courtesy adjustment",
  fee_waived: "Fee waived",
  settlement: "Settlement",
  write_off: "Write-off",
  other: "Adjustment",
};

/** What a void does with money already paid on the invoice. */
export const VOID_DISPOSITIONS = ["refund", "credit", "transfer"] as const;
export type VoidDisposition = (typeof VOID_DISPOSITIONS)[number];

export const INVOICE_EVENT_KINDS = ["refund", "credit_held", "credit_applied", "adjustment", "void"] as const;
export type InvoiceEventKind = (typeof INVOICE_EVENT_KINDS)[number];

const amount = z.number().int().min(1, "Enter an amount more than $0.00").max(MAX_INVOICE_CENTS);
// A reason is required on every action; it stays inside the firm unless staff choose to show it.
const reason = z.string().trim().min(1, "Enter the reason. It stays inside the firm unless you choose to show it to the client.").max(2000);
const shown = z.boolean().default(false);

export const refundSchema = z
  .object({
    amountCents: amount,
    method: z.enum(REFUND_METHODS),
    /** The card payment to refund (required for a Stripe refund). */
    paymentId: z.string().uuid().optional(),
    reference: z.string().trim().max(200).optional(),
    reason,
    showToClient: shown,
  })
  .strict()
  .refine((b) => b.method !== "stripe" || !!b.paymentId, { message: "Choose the card payment to refund", path: ["paymentId"] });

export const holdCreditSchema = z.object({ amountCents: amount, reason, showToClient: shown }).strict();
export const applyCreditSchema = z.object({ amountCents: amount, reason, showToClient: shown }).strict();
export const adjustInvoiceSchema = z.object({ amountCents: amount, type: z.enum(ADJUSTMENT_TYPES), reason, showToClient: shown }).strict();

export const voidInvoiceSchema = z
  .object({
    reason,
    showToClient: shown,
    /** Required when money has been paid: refund it, hold it as credit, or keep it against another invoice. */
    disposition: z.enum(VOID_DISPOSITIONS).optional(),
    refundMethod: z.enum(REFUND_METHODS).optional(),
    reference: z.string().trim().max(200).optional(),
    targetInvoiceId: z.string().uuid().optional(),
  })
  .strict()
  .refine((b) => b.disposition !== "refund" || !!b.refundMethod, { message: "Choose how the money is refunded", path: ["refundMethod"] })
  .refine((b) => b.disposition !== "transfer" || !!b.targetInvoiceId, { message: "Choose the invoice to keep the money against", path: ["targetInvoiceId"] });

/** A history line as the client sees it (the reason only when staff chose to show it). */
export type ClientInvoiceEvent = {
  kind: InvoiceEventKind;
  method: string | null;
  amountCents: number;
  effectiveOn: string;
  note: string | null;
};

const shortDate = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** "Refund of $250.00 on Nov 3", "Credit of $100.00 held for your next invoice", "Courtesy adjustment of $75.00". */
export function clientEventLabel(e: Pick<ClientInvoiceEvent, "kind" | "method" | "amountCents" | "effectiveOn">): string {
  const amt = formatCents(e.amountCents);
  switch (e.kind) {
    case "refund":
      return `Refund of ${amt} on ${shortDate(e.effectiveOn)}`;
    case "credit_held":
      return `Credit of ${amt} held for your next invoice`;
    case "credit_applied":
      return `Credit of ${amt} applied from your account`;
    case "adjustment":
      switch (e.method as AdjustmentType) {
        case "courtesy":
          return `Courtesy adjustment of ${amt}`;
        case "fee_waived":
          return `Fee of ${amt} waived`;
        case "settlement":
          return `Settlement adjustment of ${amt}`;
        case "write_off":
          return `${amt} written off`;
        default:
          return `Adjustment of ${amt}`;
      }
    case "void":
      return `Invoice cancelled on ${shortDate(e.effectiveOn)}`;
    default:
      return assertNever(e.kind);
  }
}

function assertNever(x: never): never {
  throw new Error(`Unknown invoice event: ${String(x)}`);
}
